/**
 * Number and currency formatting utilities
 * Uses UK locale (en-GB) for number formatting
 */

/**
 * Format a number with thousand separators using UK locale
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted string with commas (e.g., "1,000,000")
 */
export function formatNumber(value: number | string, decimals: number = 0): string {
  const numValue = typeof value === 'string' ? Number.parseFloat(value) : value;

  if (Number.isNaN(numValue)) {
    return '';
  }

  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numValue);
}

/**
 * Format a number as currency (GBP) with thousand separators
 * @param value - The numeric value to format
 * @param includeSymbol - Whether to include £ symbol (default: false for input fields)
 * @returns Formatted currency string (e.g., "£1,000,000" or "1,000,000")
 */
export function formatCurrency(value: number | string, includeSymbol: boolean = false): string {
  const numValue = typeof value === 'string' ? Number.parseFloat(value) : value;

  if (Number.isNaN(numValue)) {
    return '';
  }

  if (includeSymbol) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  }

  return formatNumber(numValue, 0);
}

/**
 * Remove commas and other formatting from a string to get raw number
 * @param value - The formatted string (e.g., "1,000,000")
 * @returns Cleaned string suitable for parseFloat (e.g., "1000000")
 */
export function unformatNumber(value: string): string {
  return value.replaceAll(',', '');
}

/**
 * Parse a formatted number string to a number
 * @param value - The formatted string (e.g., "1,000,000")
 * @returns Numeric value or NaN if invalid
 */
export function parseFormattedNumber(value: string): number {
  const cleaned = unformatNumber(value);
  return Number.parseFloat(cleaned);
}

/**
 * Convert a plan year number to a calendar year.
 * Year 1 of a plan with planAsOfDate "2026-01-01" is calendar year 2026.
 */
export function planYearToCalendar(planYear: number, planAsOfDate: string): number {
  // Parse the year directly from the ISO date string to avoid timezone-dependent Date parsing.
  const baseYear = parseInt(planAsOfDate.slice(0, 4), 10);
  return baseYear + planYear - 1;
}

/**
 * Convert a nominal amount to real terms (today's money) and format as currency.
 * Returns an empty string when inflationIndex <= 1 (i.e. year 1 or no inflation).
 */
export function formatRealTerms(nominal: number, inflationIndex: number): string {
  if (inflationIndex <= 1) return '';
  return formatCurrency(Math.round(nominal / inflationIndex), true);
}

/**
 * Render a CareType for display: "RESIDENTIAL" -> "Residential Care".
 *
 * CareType is an uppercase union on the wire ('HOME' | 'RESIDENTIAL' |
 * 'NURSING'), enforced by z.enum. Three places displayed it and only one
 * formatted it, so the same care period read as "Residential Care" in the year
 * detail popup and "RESIDENTIAL" in the year table and the cash flow chart.
 */
export function formatCareType(careType: string): string {
  if (!careType) return '';
  return `${careType.charAt(0)}${careType.slice(1).toLowerCase()} Care`;
}
